import { SectionLabel } from "./SectionLabel";
import { Glow } from "./Glow";
import { HeroBackground, HeroFade } from "./HeroBackground";
import { ReactNode } from "react";
import { ScrollHint } from "./ScrollHint";

interface PageHeroProps {
  label: string;
  title: string;
  titleGradient?: string;
  description: string;
  children?: ReactNode;
  titleSize?: "default" | "sm";
  /** File name inside /public/background-images */
  bgImage?: string;
  fullHeight?: boolean;
  /** Phones: shorter hero with centred copy so the next section peeks in */
  compactMobile?: boolean;
}

export function PageHero({
  label,
  title,
  titleGradient,
  description,
  children,
  titleSize = "default",
  bgImage,
  fullHeight = false,
  compactMobile = false,
}: PageHeroProps) {
  const titleWithout = titleGradient
    ? title.replace(titleGradient, "").trim()
    : title;
  const titleClass =
    titleSize === "sm"
      ? "font-display font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 3xl:text-7xl text-white mt-3 mb-4 sm:mb-5 leading-[1.1] break-words hyphens-auto"
      : "font-display font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 3xl:text-8xl text-white mt-3 mb-4 sm:mb-5 leading-[1.05]";
  return (
    <section
      className={
        fullHeight
          ? // Phones: copy starts just under the tear in the background image, which sits at ~50% height
            "relative overflow-hidden flex flex-col justify-start lg:justify-center min-h-[100svh] pt-[48svh] pb-28 sm:pb-16 sm:pt-36 md:pt-40 lg:pt-60 lg:pb-8"
          : compactMobile
            ? "relative overflow-hidden flex flex-col justify-center sm:justify-start lg:justify-center min-h-[72svh] sm:min-h-[100svh] pt-24 sm:pt-28 md:pt-32 pb-8 sm:pb-16 lg:min-h-screen lg:pt-32 lg:pb-20"
            : // Phones: copy vertically centred, nudged a little above the middle
              "relative overflow-hidden flex flex-col justify-start max-md:justify-center lg:justify-center min-h-[100svh] pt-20 sm:pt-28 md:pt-32 pb-28 sm:pb-16 lg:min-h-screen lg:pt-32 lg:pb-20"
      }
    >
      {bgImage ? (
        <>
          <HeroBackground src={bgImage} />
          <Glow color="orange" size={500} className="top-1/2 left-1/3" />
          <Glow color="pink" size={300} className="top-1/3 right-1/4" />
        </>
      ) : (
        <>
          <Glow color="orange" size={500} className="top-1/2 left-1/3" />
          <Glow color="pink" size={300} className="top-1/3 right-1/4" />
        </>
      )}
      <HeroFade />
      {/* Full-height on phones: the description drops to the bottom of the screen */}
      <div
        className={`container relative isolate z-10 text-center max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto ${fullHeight ? "flex flex-1 flex-col items-center sm:block sm:flex-none" : ""}`}
      >
        {/* Phones: dark, blurred pool behind the copy so it reads over the image */}
        {!fullHeight && (
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-4 -inset-y-20 -z-10 sm:hidden"
            style={{
              background:
                "radial-gradient(ellipse 70% 55% at 50% 50%, rgba(10,10,15,0.9) 0%, rgba(10,10,15,0.65) 50%, rgba(10,10,15,0) 100%)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              maskImage: "radial-gradient(ellipse 65% 55% at 50% 50%, #000 45%, transparent 100%)",
              WebkitMaskImage: "radial-gradient(ellipse 65% 55% at 50% 50%, #000 45%, transparent 100%)",
            }}
          />
        )}
        <SectionLabel>{label}</SectionLabel>
        <h1 className={titleClass}>
          {titleWithout.split("\n").map((line, i, arr) => (
            <span key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
          {titleGradient && (
            <>
              <br />
              <span className="text-gradient-warm">{titleGradient}</span>
            </>
          )}
        </h1>
        <p
          className={`text-base sm:text-lg 3xl:text-xl text-white/75 sm:text-white/60 leading-relaxed ${fullHeight ? "mt-auto sm:mt-0 max-w-2xl 3xl:max-w-3xl" : "max-w-xl 3xl:max-w-2xl"} mx-auto`}
        >
          {description}
        </p>
      </div>
      {children}
      <ScrollHint className={compactMobile ? "max-sm:hidden" : ""} />
    </section>
  );
}
